/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { useVirtual } from 'react-virtual';
import { __ } from '@web-stories-wp/i18n';
import { trackEvent } from '@web-stories-wp/tracking';
import { UnitsProvider } from '@web-stories-wp/units';
import { useSnackbar } from '@web-stories-wp/design-system';
import styled from 'styled-components';
/**
 * Internal dependencies
 */
import { PANE_PADDING } from '../shared';
import {
  getVirtualizedItemIndex,
  useVirtualizedGridNavigation,
  VirtualizedContainer,
  PANEL_GRID_ROW_GAP,
  VirtualizedWrapper,
} from '../shared/virtualizedPanelGrid';
import { duplicatePage } from '../../../../elements';
import { useStory } from '../../../../app/story';
import PageTemplate from './pageTemplate';
import useIsStory from '@src/hooks/useIsStory';
import { formatTextContent } from '../../../../utils/formatTextContent';
import { PageSizePropType } from '../../../../types';

const THRESHOLD = 6;
const POINTER_DRAG_THRESHOLD = 4;
const AUTO_SCROLL_EDGE_SIZE = 48;
const AUTO_SCROLL_MAX_STEP = 18;

const DragPreviewContainer = styled.div`
  position: fixed;
  top: ${({ $y, $offsetY }) => $y - $offsetY}px;
  left: ${({ $x, $offsetX }) => $x - $offsetX}px;
  width: ${({ $pageSize }) => $pageSize.width}px;
  height: ${({ $pageSize }) => $pageSize.containerHeight}px;
  z-index: 2147483647;
  pointer-events: none;
  opacity: 0.94;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.35));
`;

function getGroupBounds(items, sourceItem) {
  const groupIndexes = items
    .map((item, index) =>
      Boolean(item.isPinned) === Boolean(sourceItem.isPinned) ? index : -1
    )
    .filter((index) => index !== -1);

  if (!groupIndexes.length) {
    return null;
  }

  return {
    first: groupIndexes[0],
    last: groupIndexes[groupIndexes.length - 1],
  };
}

function getReorderedPages(items, sourceId, targetIndex) {
  if (!sourceId || targetIndex == null) {
    return items;
  }

  const fromIndex = items.findIndex(({ id }) => id === sourceId);

  if (fromIndex === -1) {
    return items;
  }

  const groupBounds = getGroupBounds(items, items[fromIndex]);
  const minIndex = groupBounds?.first ?? 0;
  const maxIndex = groupBounds?.last ?? items.length - 1;
  const toIndex = Math.max(minIndex, Math.min(targetIndex, maxIndex));

  if (fromIndex === toIndex) {
    return items;
  }

  const reorderedItems = [...items];
  const [movedItem] = reorderedItems.splice(fromIndex, 1);
  reorderedItems.splice(toIndex, 0, movedItem);

  return reorderedItems;
}

function TemplateList({
  pages,
  parentRef,
  pageSize,
  handleDelete,
  fetchTemplates,
  renderTemplateActions,
  canReorderTemplates,
  onReorderTemplate,
  ...rest
}) {
  const { addPage, replaceCurrentPage } = useStory(({ actions }) => ({
    addPage: actions.addPage,
    replaceCurrentPage: actions.replaceCurrentPage,
  }));
  const { showSnackbar } = useSnackbar();

  const containerRef = useRef();
  const pageRefs = useRef({});
  const ignoreNextClickRef = useRef(false);
  const dragTargetIndexRef = useRef(null);
  const [draggedPageId, setDraggedPageId] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [dragTargetIndex, setDragTargetIndex] = useState(null);

  const displayedPages = useMemo(
    () => getReorderedPages(pages, draggedPageId, dragTargetIndex),
    [dragTargetIndex, draggedPageId, pages]
  );

  const pageIds = useMemo(
    () => displayedPages.map((page) => page.id),
    [displayedPages]
  );

  const isStory = useIsStory();

  const handlePageClick = useCallback(
    (page) => {
      if (ignoreNextClickRef.current) {
        ignoreNextClickRef.current = false;
        return;
      }

      let duplicatedPage = duplicatePage(page);

      duplicatedPage = {
        ...duplicatedPage,
        elements: duplicatedPage.elements.map((duplicatedElement) => {
          if (duplicatedElement.type === 'text') {
            const formattedContent = formatTextContent(
              duplicatedElement.content
            );

            return {
              ...duplicatedElement,
              content: formattedContent,
            };
          }

          return duplicatedElement;
        }),
      };

      if (isStory) {
        addPage({ page: duplicatedPage });
      } else {
        replaceCurrentPage({ page: duplicatedPage });
      }

      trackEvent('insert_page_template', {
        name: page.title,
      });
      showSnackbar({
        message: __('Template added.', 'web-stories'),
        dismissable: true,
      });
    },
    [addPage, showSnackbar]
  );

  const getTargetIndexFromPoint = useCallback(
    (clientX, clientY) => {
      if (!containerRef.current || pages.length === 0) {
        return null;
      }

      const { left, top } = containerRef.current.getBoundingClientRect();
      const rowSize = pageSize.containerHeight + PANEL_GRID_ROW_GAP;
      const columnSize = pageSize.width + PANEL_GRID_ROW_GAP;
      const rowIndex = Math.max(0, Math.floor((clientY - top) / rowSize));
      const columnIndex = Math.max(
        0,
        Math.min(1, Math.floor((clientX - left) / columnSize))
      );
      const targetIndex = rowIndex * 2 + columnIndex;

      return Math.max(0, Math.min(targetIndex, pages.length - 1));
    },
    [pageSize.containerHeight, pageSize.width, pages.length]
  );

  const getAutoScrollStep = useCallback(
    (clientY) => {
      if (!parentRef.current) {
        return 0;
      }

      const { top, bottom } = parentRef.current.getBoundingClientRect();
      const distanceFromTop = clientY - top;
      const distanceFromBottom = bottom - clientY;

      if (distanceFromTop < AUTO_SCROLL_EDGE_SIZE) {
        return -Math.ceil(
          ((AUTO_SCROLL_EDGE_SIZE - distanceFromTop) / AUTO_SCROLL_EDGE_SIZE) *
            AUTO_SCROLL_MAX_STEP
        );
      }

      if (distanceFromBottom < AUTO_SCROLL_EDGE_SIZE) {
        return Math.ceil(
          ((AUTO_SCROLL_EDGE_SIZE - distanceFromBottom) /
            AUTO_SCROLL_EDGE_SIZE) *
            AUTO_SCROLL_MAX_STEP
        );
      }

      return 0;
    },
    [parentRef]
  );

  const updateDragTargetIndex = useCallback((targetIndex) => {
    dragTargetIndexRef.current = targetIndex;
    setDragTargetIndex((current) =>
      current === targetIndex ? current : targetIndex
    );
  }, []);

  const commitReorder = useCallback(
    (sourceId, targetIndex) => {
      if (targetIndex == null) {
        return;
      }

      const reorderedPages = getReorderedPages(pages, sourceId, targetIndex);
      const toIndex = reorderedPages.findIndex(({ id }) => id === sourceId);
      const fromIndex = pages.findIndex(({ id }) => id === sourceId);

      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        onReorderTemplate?.({
          template: pages[fromIndex],
          fromIndex,
          toIndex,
        });
      }
    },
    [onReorderTemplate, pages]
  );

  const handleReorderPointerDown = useCallback(
    (event, page) => {
      if (!canReorderTemplates || page.isPinned || event.button !== 0) {
        return;
      }

      const dragPreviewElement = event.currentTarget.closest(
        '[data-page-template-id]'
      );

      if (!dragPreviewElement) {
        return;
      }

      const { left, top } = dragPreviewElement.getBoundingClientRect();
      const sourceId = page.id;
      const dragState = {
        hasStarted: false,
        startX: event.clientX,
        startY: event.clientY,
        offsetX: event.clientX - left,
        offsetY: event.clientY - top,
      };
      const autoScrollState = {
        clientX: event.clientX,
        clientY: event.clientY,
        frameId: null,
        step: 0,
      };

      const stopAutoScroll = () => {
        if (autoScrollState.frameId) {
          window.cancelAnimationFrame(autoScrollState.frameId);
          autoScrollState.frameId = null;
        }
      };

      const updateTargetFromPoint = (clientX, clientY) => {
        updateDragTargetIndex(getTargetIndexFromPoint(clientX, clientY));
      };

      const runAutoScroll = () => {
        if (!autoScrollState.step || !parentRef.current) {
          autoScrollState.frameId = null;
          return;
        }

        parentRef.current.scrollTop += autoScrollState.step;
        updateTargetFromPoint(autoScrollState.clientX, autoScrollState.clientY);
        autoScrollState.frameId = window.requestAnimationFrame(runAutoScroll);
      };

      const updateAutoScroll = (clientX, clientY) => {
        autoScrollState.clientX = clientX;
        autoScrollState.clientY = clientY;
        autoScrollState.step = getAutoScrollStep(clientY);

        if (!autoScrollState.step) {
          stopAutoScroll();
          return;
        }

        if (!autoScrollState.frameId) {
          autoScrollState.frameId = window.requestAnimationFrame(runAutoScroll);
        }
      };

      const clearDragState = () => {
        stopAutoScroll();
        updateDragTargetIndex(null);
        setDraggedPageId(null);
        setDragPreview(null);

        if (dragState.hasStarted) {
          window.setTimeout(() => {
            ignoreNextClickRef.current = false;
          }, 0);
        }
      };

      const removePointerListeners = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        window.removeEventListener('pointercancel', handlePointerCancel);
      };

      const startDrag = (clientX, clientY) => {
        dragState.hasStarted = true;
        ignoreNextClickRef.current = true;
        setDraggedPageId(sourceId);
        setDragPreview({
          page,
          x: clientX,
          y: clientY,
          offsetX: dragState.offsetX,
          offsetY: dragState.offsetY,
        });
        updateTargetFromPoint(clientX, clientY);
      };

      function handlePointerMove(pointerMoveEvent) {
        const deltaX = pointerMoveEvent.clientX - dragState.startX;
        const deltaY = pointerMoveEvent.clientY - dragState.startY;

        if (
          !dragState.hasStarted &&
          Math.hypot(deltaX, deltaY) < POINTER_DRAG_THRESHOLD
        ) {
          return;
        }

        pointerMoveEvent.preventDefault();

        if (!dragState.hasStarted) {
          startDrag(pointerMoveEvent.clientX, pointerMoveEvent.clientY);
        } else {
          setDragPreview((current) =>
            current
              ? {
                  ...current,
                  x: pointerMoveEvent.clientX,
                  y: pointerMoveEvent.clientY,
                }
              : current
          );
        }

        updateTargetFromPoint(
          pointerMoveEvent.clientX,
          pointerMoveEvent.clientY
        );
        updateAutoScroll(pointerMoveEvent.clientX, pointerMoveEvent.clientY);
      }

      function handlePointerUp(pointerUpEvent) {
        if (dragState.hasStarted) {
          commitReorder(
            sourceId,
            dragTargetIndexRef.current ??
              getTargetIndexFromPoint(
                pointerUpEvent.clientX,
                pointerUpEvent.clientY
              )
          );
        }

        removePointerListeners();
        clearDragState();
      }

      function handlePointerCancel() {
        removePointerListeners();
        clearDragState();
      }

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerCancel);
    },
    [
      canReorderTemplates,
      commitReorder,
      getAutoScrollStep,
      getTargetIndexFromPoint,
      parentRef,
      updateDragTargetIndex,
    ]
  );

  const rowsTotal = useMemo(
    () => Math.ceil((displayedPages || []).length / 2),
    [displayedPages]
  );

  const rowVirtualizer = useVirtual({
    size: rowsTotal,
    parentRef,
    estimateSize: useCallback(
      () => pageSize.containerHeight + PANEL_GRID_ROW_GAP,
      [pageSize.containerHeight]
    ),
    overscan: 4,
  });

  useEffect(() => {
    if (
      rowVirtualizer.virtualItems.length &&
      rowsTotal &&
      rowsTotal - THRESHOLD <
        rowVirtualizer.virtualItems[rowVirtualizer.virtualItems.length - 1]
          .index
    ) {
      fetchTemplates?.();
    }
  }, [rowVirtualizer, rowsTotal, fetchTemplates]);

  const columnVirtualizer = useVirtual({
    horizontal: true,
    size: 2,
    parentRef,
    estimateSize: useCallback(
      () => pageSize.width + PANEL_GRID_ROW_GAP,
      [pageSize.width]
    ),
    overscan: 0,
  });

  const {
    activeGridItemId,
    handleGridFocus,
    handleGridItemFocus,
    isGridFocused,
  } = useVirtualizedGridNavigation({
    rowVirtualizer,
    containerRef,
    gridItemRefs: pageRefs,
    gridItemIds: pageIds,
  });

  const handleKeyboardPageClick = useCallback(
    ({ code }, page) => {
      if (isGridFocused) {
        if (code === 'Enter') {
          handlePageClick(page);
        } else if (code === 'Space') {
          handleDelete?.(page);
        }
      }
    },
    [isGridFocused, handlePageClick, handleDelete]
  );

  return (
    <UnitsProvider
      pageSize={{
        width: pageSize.width,
        height: pageSize.height,
      }}
    >
      <VirtualizedWrapper height={rowVirtualizer.totalSize}>
        <VirtualizedContainer
          height={rowVirtualizer.totalSize}
          ref={containerRef}
          columnWidth={pageSize.width}
          rowHeight={pageSize.containerHeight}
          paneLeft={PANE_PADDING}
          role="list"
          aria-label={__('Page Template Options', 'web-stories')}
        >
          {rowVirtualizer.virtualItems.map((virtualRow) =>
            columnVirtualizer.virtualItems.map((virtualColumn) => {
              const pageIndex = getVirtualizedItemIndex({
                columnIndex: virtualColumn.index,
                rowIndex: virtualRow.index,
              });

              const page = displayedPages[pageIndex];

              if (!page) {
                return null;
              }
              const isActive = activeGridItemId === page.id && isGridFocused;
              const isReorderable = canReorderTemplates && !page.isPinned;

              return (
                <PageTemplate
                  key={page.id}
                  data-testid={`page_template_${page.id}`}
                  ref={(el) => (pageRefs.current[page.id] = el)}
                  translateY={virtualRow.start}
                  translateX={virtualColumn.start}
                  page={page}
                  pageSize={pageSize}
                  isActive={isActive}
                  isReorderable={isReorderable}
                  isDragging={draggedPageId === page.id}
                  onFocus={() => handleGridItemFocus(page.id)}
                  onClick={() => handlePageClick(page)}
                  onKeyUp={(event) => handleKeyboardPageClick(event, page)}
                  onReorderPointerDown={(event) =>
                    handleReorderPointerDown(event, page)
                  }
                  handleDelete={handleDelete}
                  renderActions={renderTemplateActions}
                  {...rest}
                />
              );
            })
          )}
        </VirtualizedContainer>
      </VirtualizedWrapper>
      {dragPreview && (
        <DragPreviewContainer
          $x={dragPreview.x}
          $y={dragPreview.y}
          $offsetX={dragPreview.offsetX}
          $offsetY={dragPreview.offsetY}
          $pageSize={pageSize}
        >
          <PageTemplate
            aria-hidden="true"
            role="presentation"
            tabIndex={-1}
            page={dragPreview.page}
            pageSize={pageSize}
            translateY={0}
            translateX={0}
            isActive
          />
        </DragPreviewContainer>
      )}
    </UnitsProvider>
  );
}

TemplateList.propTypes = {
  parentRef: PropTypes.object.isRequired,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isPinned: PropTypes.bool,
    })
  ),
  pageSize: PageSizePropType.isRequired,
  handleDelete: PropTypes.func,
  fetchTemplates: PropTypes.func,
  renderTemplateActions: PropTypes.func,
  canReorderTemplates: PropTypes.bool,
  onReorderTemplate: PropTypes.func,
};

export default TemplateList;
